'use client';

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { LinkStyled } from '@components/common';
import useWindowDimensions from '@hooks/useMediaQuery';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { PhoneIcon } from '@heroicons/react/24/outline';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { ApiOwnResumeDetailOk } from '@root/src/utils';
import { WaveAnimation } from './WaveAnimation';
import BestieAvatar from '../bestie/BestieAvatar';

const INTERVIEWS_PHONE_NUMBER = process.env.NEXT_PUBLIC_INTERVIEWS_PHONE_NUMBER;

interface BestieBannerProps {
  userOwnResume: ApiOwnResumeDetailOk | null;
}

/**
 * @description Bestie banner component for jobs list page
 */
export const BestieBanner = (props: BestieBannerProps) => {
  const { userOwnResume } = props;
  const { t } = useTranslation('common');
  const { isTablet } = useWindowDimensions();
  const { isAuth } = useAuthClient();

  const visibleInterviewVideos = userOwnResume?.privateInformation?.interviewVideos?.filter((video) => !video.hidden) || [];

  const hasPartialInterview = isAuth && visibleInterviewVideos.length > 0 && visibleInterviewVideos.length < 3;
  const hasCompleteInterview = isAuth && visibleInterviewVideos.length === 3;

  const interviewTitle = useMemo(() => {
    if (hasPartialInterview) return t('homepage.hero.title.resume');

    return t('homepage.hero.title.no-interview');
  }, [hasPartialInterview, t]);

  const interviewCta = useMemo(() => {
    if (hasPartialInterview) return t('my-interviews.own.cta');

    return t('ai.interview.start');
  }, [hasPartialInterview, t]);

  // If interview is complete, don't show banner
  if (hasCompleteInterview) return null;

  return (
    <>
      <div className="relative flex w-full flex-col items-center justify-center overflow-hidden rounded-md bg-surface px-4 py-6 text-ink md:p-6">
        {/* Wave Animation at bottom */}
        <WaveAnimation waveCount={3} height={100} opacity={0.2} />
        <div className="flex size-full flex-col items-center justify-center gap-3 text-center md:gap-4">
          {/* Bestie Character */}
          <div className="relative flex flex-col items-center md:flex-row md:gap-12">
            {/* Bestie Image */}
            <div className="relative flex shrink-0 items-center justify-center">
              <BestieAvatar
                className="mb-3 size-56 border-2 border-secondary md:size-90"
              />
            </div>

            <div className="flex flex-col items-center">
              <h1 className="mb-4 text-xl md:mb-8 md:text-3xl">
                {interviewTitle}
              </h1>
              <div className="mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
                {isTablet && (
                  <h2>
                    {t('homepage.hero.interview.or.call')}
                  </h2>
                )}
                {!isTablet && (
                  <>
                    <LinkStyled
                      href="/messages?type=talent&bestie=true"
                      styling="solid"
                      color="secondary"
                      rounding="full"
                      className="flex items-center gap-2"
                      onClick={() => {
                        sendGoogleTagEvent('bestie_conversations_banner_interview', 'Custom');
                      }}
                    >
                      {interviewCta}
                    </LinkStyled>
                    <span>{t('homepage.hero.or.call')}</span>
                  </>
                )}
                <LinkStyled
                  href={`tel:${INTERVIEWS_PHONE_NUMBER}`}
                  styling="text"
                  color="secondary"
                  className="flex items-center gap-1 text-base"
                  onClick={() => {
                    sendGoogleTagEvent('bestie_conversations_banner_call', 'Custom');
                  }}
                >
                  <PhoneIcon className="size-4" />
                  {INTERVIEWS_PHONE_NUMBER}
                </LinkStyled>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

